export const CONTINENTS = [
    'Africa',
    'Antarctica',
    'Asia',
    'Europe',
    'North America',
    'Australia (Oceania)',
    'South America',
];

/** Maps continent name -> country names (must match useCountries.js spellings). */
export const CONTINENT_COUNTRIES = {
    Africa: [
        'Algeria', 'Angola', 'Benin', 'Botswana', 'Burkina Faso', 'Burundi', 'Cabo Verde',
        'Cameroon', 'Central African Republic', 'Chad', 'Comoros', 'Côte d\'Ivoire',
        'Democratic Republic of the Congo', 'Djibouti', 'Egypt', 'Equatorial Guinea', 'Eritrea',
        'Eswatini', 'Ethiopia', 'Gabon', 'Gambia', 'Ghana', 'Guinea', 'Guinea-Bissau', 'Kenya',
        'Lesotho', 'Liberia', 'Libya', 'Madagascar', 'Malawi', 'Mali', 'Mauritania', 'Mauritius',
        'Morocco', 'Mozambique', 'Namibia', 'Niger', 'Nigeria', 'Republic of the Congo', 'Rwanda',
        'São Tomé and Príncipe', 'Senegal', 'Seychelles', 'Sierra Leone', 'Somalia', 'South Africa',
        'South Sudan', 'Sudan', 'Tanzania', 'Togo', 'Tunisia', 'Uganda', 'Zambia', 'Zimbabwe',
    ],
    Asia: [
        'Afghanistan', 'Armenia', 'Azerbaijan', 'Bahrain', 'Bangladesh', 'Bhutan', 'Brunei',
        'Cambodia', 'China', 'Georgia', 'India', 'Indonesia', 'Iran', 'Iraq', 'Israel', 'Japan',
        'Jordan', 'Kazakhstan', 'Kuwait', 'Kyrgyzstan', 'Laos', 'Lebanon', 'Malaysia', 'Maldives',
        'Mongolia', 'Myanmar', 'Nepal', 'North Korea', 'Oman', 'Pakistan', 'Palestine',
        'Philippines', 'Qatar', 'Saudi Arabia', 'Singapore', 'South Korea', 'Sri Lanka', 'Syria',
        'Tajikistan', 'Thailand', 'Timor-Leste', 'Türkiye', 'Turkmenistan', 'United Arab Emirates',
        'Uzbekistan', 'Vietnam', 'Yemen',
    ],
    Europe: [
        'Albania', 'Andorra', 'Austria', 'Belarus', 'Belgium', 'Bosnia and Herzegovina', 'Bulgaria',
        'Croatia', 'Cyprus', 'Czechia', 'Denmark', 'Estonia', 'Finland', 'France', 'Germany',
        'Greece', 'Hungary', 'Iceland', 'Ireland', 'Italy', 'Latvia', 'Liechtenstein', 'Lithuania',
        'Luxembourg', 'Malta', 'Moldova', 'Monaco', 'Montenegro', 'Netherlands', 'North Macedonia',
        'Norway', 'Poland', 'Portugal', 'Romania', 'Russia', 'San Marino', 'Serbia', 'Slovakia',
        'Slovenia', 'Spain', 'Sweden', 'Switzerland', 'Ukraine', 'United Kingdom',
        'Vatican City (Holy See)',
    ],
    'North America': [
        'Antigua and Barbuda', 'Bahamas', 'Barbados', 'Belize', 'Canada', 'Costa Rica', 'Cuba',
        'Dominica', 'Dominican Republic', 'El Salvador', 'Grenada', 'Guatemala', 'Haiti',
        'Honduras', 'Jamaica', 'Mexico', 'Nicaragua', 'Panama', 'Saint Kitts and Nevis',
        'Saint Lucia', 'Saint Vincent and the Grenadines', 'Trinidad and Tobago', 'United States',
    ],
    'Australia (Oceania)': [
        'Australia', 'Fiji', 'Kiribati', 'Marshall Islands', 'Micronesia (Federated States of)',
        'Nauru', 'New Zealand', 'Palau', 'Papua New Guinea', 'Samoa', 'Solomon Islands', 'Tonga',
        'Tuvalu', 'Vanuatu',
    ],
    'South America': [
        'Argentina', 'Bolivia', 'Brazil', 'Chile', 'Colombia', 'Ecuador', 'Guyana', 'Paraguay',
        'Peru', 'Suriname', 'Uruguay', 'Venezuela',
    ],
    Antarctica: ['Antarctica'],
};

export const getCountriesForContinent = (continent) => {
    if (!continent) return [];
    if (continent === 'Oceania') return CONTINENT_COUNTRIES['Australia (Oceania)'] || [];
    return CONTINENT_COUNTRIES[continent] || [];
};

const normalizeCountryKey = (value) =>
    String(value || '')
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, ' ')
        .trim();

/**
 * Prefer geography-master / lead-form country names (tenant overrides included),
 * optionally filtered to a continent. Falls back to the static continent list
 * only when the geography list is empty.
 */
export const filterGeographyCountriesForContinent = (
    continent,
    geographyCountries = []
) => {
    const geoNames = (geographyCountries || [])
        .map((item) => (typeof item === 'string' ? item : item?.label || item?.name || ''))
        .map((name) => String(name || '').trim())
        .filter(Boolean);

    const uniqueGeo = [];
    const seen = new Set();
    geoNames.forEach((name) => {
        const key = normalizeCountryKey(name);
        if (!key || seen.has(key)) return;
        seen.add(key);
        uniqueGeo.push(name);
    });

    if (!continent) {
        return uniqueGeo.sort((a, b) => a.localeCompare(b));
    }

    const continentList = getCountriesForContinent(continent);
    const continentKeys = continentList.map(normalizeCountryKey).filter(Boolean);

    if (!uniqueGeo.length) {
        return [...continentList].sort((a, b) => a.localeCompare(b));
    }

    const matched = uniqueGeo.filter((name) => {
        const key = normalizeCountryKey(name);
        return continentKeys.some((continentKey) => key === continentKey);
    });

    return matched.sort((a, b) => a.localeCompare(b));
};

export const isCountryInContinentList = (country, continentCountries = []) => {
    const key = normalizeCountryKey(country);
    if (!key) return false;
    return continentCountries.some((name) => {
        const other = normalizeCountryKey(name);
        return key === other;
    });
};

export const findContinentForCountry = (country) => {
    if (!country) return null;
    const key = normalizeCountryKey(country);
    const entry = Object.entries(CONTINENT_COUNTRIES).find(([, countries]) =>
        countries.some((name) => {
            const other = normalizeCountryKey(name);
            return key === other;
        })
    );
    return entry ? entry[0] : null;
};

export const normalizeContinentLabel = (continent) => {
    if (!continent) return continent;
    return continent === 'Oceania' ? 'Australia (Oceania)' : continent;
};

/** @deprecated Prefer hooks/useLanguages — kept for any legacy imports. */
export { WORLD_LANGUAGES as COMMON_LANGUAGES } from './worldLanguages';
